import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { PLAN_PAGE } from '../../treatment/mockData';
import type { PlanPage } from '../../treatment/types';

/**
 * The patient-facing plan page (amendment 27 §6, board `TC5PlanPage`).
 *
 * **Nothing shows before the date of birth — not even the name.** The link is
 * one patient's; the DOB gate is what keeps it private, so the plan, the cost and
 * every button live behind it. Phone first; centred on desktop.
 *
 * The nine states are driven by `state` (the prototype sets it with `?state=`);
 * in production the server decides which to render.
 */
export type PlanState = 'dob' | 'plan' | 'book' | 'question' | 'wrong' | 'locked' | 'expired' | 'called';

export interface PlanPageViewProps {
  state: PlanState;
  data?: PlanPage;
  /** The correct DOB unlocks the plan (mock only — the server checks in production). */
  onOpen?: (dob: string) => void;
  onBook?: () => void;
  onCallMe?: () => void;
  onAskQuestion?: () => void;
}

export function PlanPageView({ state, data = PLAN_PAGE, onOpen, onBook, onCallMe, onAskQuestion }: PlanPageViewProps) {
  const color = data.practiceColor;
  return (
    <div className="min-h-screen w-full" style={{ background: '#F4F5F7' }}>
      <div className="mx-auto w-full max-w-[480px] px-4 py-6">
        {/* practice header — the ONLY thing shown before the DOB */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[13px] font-bold text-white" style={{ background: color }}>
              {data.practiceInitials}
            </span>
            <span className="text-[14px] font-semibold text-[#242936]">{data.practiceName}</span>
          </div>
          <span className="text-[12px] font-semibold text-[#6B7280]">English ▾</span>
        </div>

        <div className="mt-5 overflow-hidden rounded-[18px] border border-[#E7EAF2] bg-white shadow-[0_1px_3px_rgba(36,41,54,0.06)]">
          {state === 'dob' ? <DobScreen data={data} onOpen={onOpen} /> : null}
          {state === 'wrong' ? <DobScreen data={data} onOpen={onOpen} error="That date doesn’t match. 2 tries left." /> : null}
          {state === 'locked' ? <LockedScreen data={data} /> : null}
          {state === 'expired' ? <ExpiredScreen data={data} /> : null}
          {state === 'called' ? <CalledScreen data={data} /> : null}
          {state === 'plan' ? <PlanScreen data={data} onBook={onBook} onCallMe={onCallMe} onAskQuestion={onAskQuestion} /> : null}
          {state === 'book' ? <BookScreen data={data} onBook={onBook} /> : null}
          {state === 'question' ? <QuestionScreen /> : null}
        </div>

        <p className="m-0 mt-4 text-center text-[11px] text-[#9AA1AE]">
          Questions? Call {data.practicePhone}
        </p>
      </div>
    </div>
  );
}

function DobScreen({ data, onOpen, error }: { data: PlanPage; onOpen?: ((dob: string) => void) | undefined; error?: string | undefined }) {
  const [mm, setMm] = useState('');
  const [dd, setDd] = useState('');
  const [yy, setYy] = useState('');
  return (
    <div className="p-6 text-center">
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full" style={{ background: `${data.practiceColor}1A`, color: data.practiceColor }}>
        <Icon kind="lock" size={20} stroke={2} />
      </span>
      <h1 className="m-0 mt-3 text-[19px] font-bold text-[#242936]">Your treatment plan</h1>
      <p className="m-0 mt-1.5 text-[13px] leading-[1.6] text-[#5B6270]">To keep your details private, enter your date of birth.</p>
      <div className="mt-4 flex justify-center gap-2">
        {[['Month', 'MM', mm, setMm, 2], ['Day', 'DD', dd, setDd, 2], ['Year', 'YYYY', yy, setYy, 4]].map(([label, ph, val, set, len]) => (
          <label key={label as string} className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9AA1AE]">
            {label as string}
            <input
              inputMode="numeric"
              maxLength={len as number}
              value={val as string}
              placeholder={ph as string}
              onChange={(e) => { (set as (v: string) => void)(e.target.value.replace(/\D/g, '')); }}
              className="mt-1 block w-[64px] rounded-[10px] border border-[#E7EAF2] px-2 py-2 text-center font-[var(--font-mono)] text-[16px] text-[#242936] outline-none focus:border-[color:var(--practice)]"
              style={{ ['--practice' as string]: data.practiceColor }}
            />
          </label>
        ))}
      </div>
      {error !== undefined ? <div className="mt-2 text-[12px] font-semibold text-[#C0392B]">{error}</div> : null}
      <button
        type="button"
        onClick={() => { onOpen?.(`${mm}/${dd}/${yy}`); }}
        className="mt-4 w-full rounded-[11px] py-3 text-[14px] font-semibold text-white"
        style={{ background: data.practiceColor }}
      >
        Open my plan
      </button>
      <p className="m-0 mt-3 text-[11px] text-[#9AA1AE]">This link is only for you and works until {data.expiresOn}.</p>
    </div>
  );
}

/** The tooth chart (spec 48 §1.2): two rows of 16, no numbers on the teeth, the
 * treated teeth filled in the practice colour and carrying their card's number. */
function ToothChart({ data }: { data: PlanPage }) {
  const color = data.practiceColor;
  const hasArea = data.procedures.some((p) => p.chart.area === true);
  const scrollTo = (n: number) => {
    document.getElementById(`card-${String(n)}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderArch = (arch: 'upper' | 'lower') => {
    // position (1..16) → filled (exact tooth) or shaded (area). Exact wins if both.
    const filled = new Set<number>();
    const shaded = new Set<number>();
    for (const p of data.procedures) {
      if (p.chart.arch !== arch) continue;
      for (const t of p.chart.teeth) (p.chart.area === true ? shaded : filled).add(t);
    }
    const runs = data.procedures.filter((p) => p.chart.arch === arch);
    return (
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9AA1AE]">{arch === 'upper' ? 'Upper teeth' : 'Lower teeth'}</div>
        <div className="grid gap-[3px]" style={{ gridTemplateColumns: 'repeat(16, 1fr)' }}>
          {Array.from({ length: 16 }, (_, i) => {
            const pos = i + 1;
            const isFilled = filled.has(pos);
            const isShaded = !isFilled && shaded.has(pos);
            return (
              <div
                key={pos}
                className="h-5 rounded-[4px] rounded-b-[7px]"
                style={
                  isFilled
                    ? { background: color }
                    : isShaded
                      ? { background: `${color}22`, border: `1px dashed ${color}` }
                      : { background: '#E7EAF2' }
                }
                aria-hidden
              />
            );
          })}
        </div>
        {/* number markers, aligned to the same 16-column grid */}
        <div className="mt-1 grid gap-[3px]" style={{ gridTemplateColumns: 'repeat(16, 1fr)' }}>
          {runs.map((p) => {
            const start = Math.min(...p.chart.teeth);
            const end = Math.max(...p.chart.teeth);
            return (
              <button
                key={p.n}
                type="button"
                onClick={() => { scrollTo(p.n); }}
                aria-label={`Treatment ${String(p.n)} — go to the card`}
                className="flex items-center justify-center"
                style={{ gridColumn: `${String(start)} / ${String(end + 1)}` }}
              >
                <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ background: color }}>{p.n}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4 rounded-[14px] border border-[#E7EAF2] p-3.5">
      <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9AA1AE]">
        <span>Your right</span><span>Your left</span>
      </div>
      <div className="flex flex-col gap-3">
        {renderArch('upper')}
        {renderArch('lower')}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#9AA1AE]">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: color }} />Needs treatment</span>
        {hasArea ? (
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: `${color}22`, border: `1px dashed ${color}` }} />In this area</span>
        ) : null}
        <span>tap a number</span>
      </div>
    </div>
  );
}

function PlanScreen({ data, onBook, onCallMe, onAskQuestion }: { data: PlanPage; onBook?: (() => void) | undefined; onCallMe?: (() => void) | undefined; onAskQuestion?: (() => void) | undefined }) {
  return (
    <div className="p-5">
      <div className="text-[13px] text-[#5B6270]">Hi {data.firstName},</div>
      <div className="mt-0.5 text-[13px] text-[#5B6270]">here’s what {data.diagnosedBy} recommended on {data.diagnosedOn}</div>
      <div className="mt-1 text-[15px] font-bold text-[#242936]">
        {data.procedures.length} things · about {data.totalYouPay} for you after insurance
      </div>

      {/* tooth chart — where the treatment is, without numbers */}
      <ToothChart data={data} />

      {data.procedures.map((p) => (
        <div key={p.n} id={`card-${String(p.n)}`} className="mt-4 scroll-mt-4 rounded-[14px] border border-[#E7EAF2] p-3.5">
          <div className="flex items-start gap-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white" style={{ background: data.practiceColor }}>{p.n}</span>
            <span className="text-[14px] font-semibold leading-[1.3] text-[#242936]">{p.title}</span>
          </div>
          {p.includes !== undefined ? (
            <div className="mt-1.5 pl-[34px] text-[12px] text-[#9AA1AE]">{p.includes}</div>
          ) : null}
          {/* the x-ray placeholder — an image the backend supplies */}
          {p.xrayCaption !== undefined ? (
            <div className="mt-2.5 flex h-28 items-center justify-center rounded-[10px]" style={{ background: p.xrayTint ?? '#EEF0F3' }}>
              <span className="text-[11px] font-semibold text-[#6B7280]">{p.xrayCaption} · tap to zoom</span>
            </div>
          ) : null}
          <div className="mt-2.5">
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9AA1AE]">Why</div>
            <p className="m-0 mt-0.5 text-[12.5px] leading-[1.6] text-[#5B6270]">{p.why}</p>
          </div>
          {p.ifYouWait.trim() !== '' ? (
            <div className="mt-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9AA1AE]">If you wait</div>
              <p className="m-0 mt-0.5 text-[12.5px] leading-[1.6] text-[#5B6270]">{p.ifYouWait}</p>
            </div>
          ) : null}
        </div>
      ))}

      {/* cost */}
      <div className="mt-4 rounded-[14px] border border-[#E7EAF2] p-3.5">
        <div className="text-[13px] font-bold text-[#242936]">What it costs</div>
        <div className="mt-2 grid grid-cols-[1fr_0.6fr_0.6fr_0.6fr] gap-x-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#9AA1AE]">
          <span /><span className="text-right">Fee</span><span className="text-right">Ins.</span><span className="text-right">You</span>
        </div>
        {data.procedures.map((p) => (
          <div key={p.n} className="grid grid-cols-[1fr_0.6fr_0.6fr_0.6fr] gap-x-2 border-t border-[#F0F1F4] py-1.5 text-[12.5px]">
            <span className="text-[#5B6270]">
              <span className="font-[var(--font-mono)] text-[#9AA1AE]">{p.n} · </span>{p.costName}
              <span className="block text-[11px] text-[#9AA1AE]">{p.costSub}</span>
            </span>
            <span className="text-right font-[var(--font-mono)]">{p.fee}</span>
            <span className="text-right font-[var(--font-mono)]">{p.insurance}</span>
            <span className="text-right font-[var(--font-mono)] font-bold text-[#242936]">{p.youPay}</span>
          </div>
        ))}
        <div className="grid grid-cols-[1fr_0.6fr_0.6fr_0.6fr] gap-x-2 border-t-2 border-[#E7EAF2] py-1.5 text-[12.5px] font-bold">
          <span>Total</span>
          <span className="text-right font-[var(--font-mono)]">{data.totalFee}</span>
          <span className="text-right font-[var(--font-mono)]">{data.totalInsurance}</span>
          <span className="text-right font-[var(--font-mono)]" style={{ color: data.practiceColor }}>{data.totalYouPay}</span>
        </div>
        <div className="mt-2.5 rounded-[10px] px-3 py-2 text-[12px] leading-[1.55]" style={{ background: `${data.practiceColor}12`, color: '#3A4150' }}>
          Your insurance has <b>{data.benefitsLeft} left this year</b>. It resets on {data.benefitsReset}.
        </div>
        <p className="m-0 mt-1.5 text-[11px] leading-[1.5] text-[#9AA1AE]">
          Checked with {data.insuranceCarrier} on {data.insuranceCheckedOn}. Insurance amounts are estimates — the final amount can change.
        </p>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <button type="button" onClick={onBook} className="w-full rounded-[11px] py-3 text-[14px] font-semibold text-white" style={{ background: data.practiceColor }}>Book my visit</button>
        <button type="button" onClick={onCallMe} className="w-full rounded-[11px] border py-3 text-[14px] font-semibold" style={{ borderColor: data.practiceColor, color: data.practiceColor }}>Call me</button>
        <button type="button" onClick={onAskQuestion} className="w-full py-2 text-[13px] font-semibold text-[#6B7280]">I have a question</button>
      </div>
    </div>
  );
}

function BookScreen({ data, onBook }: { data: PlanPage; onBook?: (() => void) | undefined }) {
  const days = [['Tue', '6'], ['Wed', '7'], ['Thu', '8'], ['Mon', '12']];
  const times = ['8:00', '9:30', '1:00', '2:30', '4:00'];
  const [day, setDay] = useState('6');
  const [time, setTime] = useState('9:30');
  return (
    <div className="p-5">
      <button type="button" onClick={onBook} className="text-[12.5px] font-semibold" style={{ color: data.practiceColor }}>← Back to your plan</button>
      <h1 className="m-0 mt-2 text-[17px] font-bold text-[#242936]">Book your crown visit</h1>
      <p className="m-0 mt-1 text-[12.5px] leading-[1.6] text-[#5B6270]">
        {data.bookLengthMins} minutes with {data.bookProvider}. The deep cleaning is done at the same visit.
      </p>
      <div className="mt-3 text-[12px] font-semibold text-[#9AA1AE]">October</div>
      <div className="mt-1.5 flex gap-2">
        {days.map(([d, n]) => (
          <button key={n} type="button" onClick={() => { setDay(n ?? ''); }} className={cn('flex-1 rounded-[10px] border py-2 text-center', day === n ? 'text-white' : 'text-[#242936]')} style={day === n ? { background: data.practiceColor, borderColor: data.practiceColor } : { borderColor: '#E7EAF2' }}>
            <span className="block text-[11px]">{d}</span><span className="block text-[15px] font-bold">{n}</span>
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {times.map((tm) => (
          <button key={tm} type="button" onClick={() => { setTime(tm); }} className={cn('rounded-[9px] border px-3 py-1.5 text-[13px] font-semibold', time === tm ? 'text-white' : 'text-[#242936]')} style={time === tm ? { background: data.practiceColor, borderColor: data.practiceColor } : { borderColor: '#E7EAF2' }}>{tm}</button>
        ))}
      </div>
      <button type="button" className="mt-4 w-full rounded-[11px] py-3 text-[14px] font-semibold text-white" style={{ background: data.practiceColor }}>Book Tue {day} Oct, {time} am</button>
      <p className="m-0 mt-2 text-[11px] text-[#9AA1AE]">You’ll get a text to confirm. Need another time? Tap Call me on your plan.</p>
    </div>
  );
}

function QuestionScreen() {
  return (
    <div className="p-5">
      <h1 className="m-0 text-[17px] font-bold text-[#242936]">Ask us anything</h1>
      <p className="m-0 mt-1 text-[12.5px] leading-[1.6] text-[#5B6270]">About the crown, the cleaning, or the cost. We’ll reply by text.</p>
      <textarea rows={4} className="mt-3 w-full rounded-[11px] border border-[#E7EAF2] px-3 py-2.5 text-[13.5px] outline-none" placeholder="Type your question…" />
      <button type="button" className="mt-2 w-full rounded-[11px] py-3 text-[14px] font-semibold text-white" style={{ background: PLAN_PAGE.practiceColor }}>Send</button>
      <p className="m-0 mt-2 text-[11px] text-[#9AA1AE]">A person from our team reads every question.</p>
    </div>
  );
}

function LockedScreen({ data }: { data: PlanPage }) {
  return (
    <div className="p-6 text-center">
      <h1 className="m-0 text-[18px] font-bold text-[#C0392B]">Locked for an hour</h1>
      <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[#5B6270]">For your privacy, this page is locked after 3 tries. Try again after 3:42 pm, or call us.</p>
      <div className="mt-3 text-[14px] font-semibold" style={{ color: data.practiceColor }}>{data.practicePhone}</div>
    </div>
  );
}

function ExpiredScreen({ data }: { data: PlanPage }) {
  return (
    <div className="p-6 text-center">
      <h1 className="m-0 text-[18px] font-bold text-[#242936]">This link has expired</h1>
      <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[#5B6270]">Links last 30 days. Reply to our last text, or call us, and we’ll send you a new one.</p>
      <div className="mt-3 text-[14px] font-semibold" style={{ color: data.practiceColor }}>{data.practicePhone}</div>
    </div>
  );
}

function CalledScreen({ data }: { data: PlanPage }) {
  return (
    <div className="p-6 text-center">
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full" style={{ background: `${data.practiceColor}1A`, color: data.practiceColor }}>
        <Icon kind="phone" size={20} stroke={2} />
      </span>
      <h1 className="m-0 mt-3 text-[18px] font-bold text-[#242936]">We’ll call you</h1>
      <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[#5B6270]">Someone from {data.practiceName} will call you today, before 5 pm.</p>
      <p className="m-0 mt-1 text-[12px] text-[#9AA1AE]">Asked after 4 pm? We call the next morning.</p>
    </div>
  );
}
